import React from "react";
import { Badge, PaginationItem, PaginationLink } from "reactstrap";
import defaultAvatar from "assets/img/brand/avatar-2.jpg";

export const styles = {
    statCard: {
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)",
        borderRadius: "16px",
        border: "1px solid rgba(0,0,0,0.04)",
        boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
        height: "100%",
        padding: "24px",
        position: "relative",
        overflow: "hidden"
    },
    statIconBox: {
        width: "48px",
        height: "48px",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "20px",
        color: "#fff",
        boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        marginBottom: "16px"
    },
    tableRow: {
        transition: "all 0.15s ease",
        position: "relative",
    },
    avatar: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: "14px",
        color: "#fff",
        flexShrink: 0,
    },
    signalBadge: {
        background: "#eef1f8",
        color: "#32325d",
        border: "1px solid #dee2e6",
        padding: "4px 8px",
        borderRadius: "6px",
        fontSize: "11px",
        marginRight: "6px",
        fontWeight: 600,
        display: "inline-block"
    },
    infoBlock: {
        background: "#fff",
        borderRadius: "8px",
        padding: "16px",
        border: "1px solid #e9ecef",
        marginBottom: "12px",
    },
    auditTrailLine: {
        borderLeft: "2px solid #e9ecef",
        paddingLeft: "16px",
        marginLeft: "8px",
        paddingBottom: "12px",
        position: "relative"
    },
    auditDot: {
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        background: "#5e72e4",
        position: "absolute",
        left: "-6px",
        top: "4px",
        border: "2px solid #fff"
    },
    filterBar: {
        background: "linear-gradient(135deg, #f8f9fe 0%, #eef1f8 100%)",
        borderRadius: "12px",
        padding: "20px 24px",
        margin: "0 0 4px 0",
        border: "1px solid rgba(0,0,0,0.04)",
        position: "relative",
        zIndex: 1,
        overflowX: "auto",
        whiteSpace: "nowrap",
        WebkitOverflowScrolling: "touch",
    },
    filterPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        background: "#fff",
        borderRadius: "10px",
        padding: "10px 18px",
        border: "1px solid #e2e8f0",
        fontSize: "14px",
        fontWeight: 600,
        color: "#525f7f",
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    filterPillActive: {
        background: "linear-gradient(135deg, #11cdef 0%, #1171ef 100%)",
        color: "#fff",
        border: "1px solid transparent",
        boxShadow: "0 4px 12px rgba(17,205,239,0.3)",
    },
    auditCard: {
        background: "#fff",
        borderLeft: "4px solid #5e72e4",
        borderRadius: "8px",
        padding: "16px",
        marginBottom: "16px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.02)"
    }
};

export const getTierBadge = (tier, color) => {
    switch (tier) {
        case "Platinum":
            return <Badge style={{ background: "linear-gradient(135deg, #8965e0, #bc00dd)", color: "#fff", padding: "6px 10px", borderRadius: "6px" }}><i className="fas fa-gem mr-1" />{tier}</Badge>;
        case "Gold":
            return <Badge style={{ background: "linear-gradient(135deg, #ffd700, #ff8c00)", color: "#fff", padding: "6px 10px", borderRadius: "6px" }}><i className="fas fa-medal mr-1" />{tier}</Badge>;
        case "Silver":
            return <Badge style={{ background: "linear-gradient(135deg, #c0c0c0, #808080)", color: "#fff", padding: "6px 10px", borderRadius: "6px" }}><i className="fas fa-award mr-1" />{tier}</Badge>;
        default:
            const bgColor = color || "#525f7f";
            return <Badge style={{
                backgroundColor: `${bgColor}12`,
                color: bgColor,
                border: `1px solid ${bgColor}30`,
                padding: "6px 14px",
                borderRadius: "8px",
                textTransform: 'capitalize',
                fontWeight: '800',
                fontSize: '11px',
                letterSpacing: '0.3px'
            }}>
                {tier || "Standard"}
            </Badge>;
    }
};

export const getUserImage = (image) => {
    if (!image) return defaultAvatar;
    if (image.startsWith("http")) return image;
    
    const base = process.env.REACT_APP_IMAGE_BASE_URL || "https://cradleaibucket.s3.us-east-1.amazonaws.com";
    return `${base}/users-profile-images/${image}`;
};

export const renderPaginationItems = (currentPage, totalItems, limit, onPageChange) => {
    const totalPagesCount = Math.ceil(totalItems / limit);
    if (totalPagesCount <= 1) return null;

    const pages = [];
    const delta = 2;

    pages.push(
        <PaginationItem key={1} active={currentPage === 1}>
            <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(1); }}>1</PaginationLink>
        </PaginationItem>
    );

    if (currentPage > delta + 2) {
        pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
    }

    const rangeStart = Math.max(2, currentPage - delta);
    const rangeEnd = Math.min(totalPagesCount - 1, currentPage + delta);

    for (let i = rangeStart; i <= rangeEnd; i++) {
        pages.push(
            <PaginationItem key={i} active={currentPage === i}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(i); }}>{i}</PaginationLink>
            </PaginationItem>
        );
    }

    if (currentPage < totalPagesCount - delta - 1) {
        pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
    }

    if (totalPagesCount > 1) {
        pages.push(
            <PaginationItem key={totalPagesCount} active={currentPage === totalPagesCount}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(totalPagesCount); }}>{totalPagesCount}</PaginationLink>
            </PaginationItem>
        );
    }

    return pages;
};
